// app/features/templates/services_and_hooks/templatesService.ts
import { apiGet } from '@/app/shared/utils/api-helpers';

import {
  FetchTemplatesParams,
  FetchTemplatesResponse,
  FileDownloadInfo,
  Template,
  TemplateCategory,
  TemplateSeoData,
} from '../templates.interface';

const API_BASE_PATH = '/api/templates';

const getBaseUrl = (): string => {
  if (typeof window === 'undefined') {
    return process.env.NEXT_PUBLIC_APP_URL || 'http://localhost:3000';
  }
  return '';
};

/**
 * Service object for all CLIENT-SIDE template-related API interactions.
 * This service ONLY calls our application's API routes via fetch.
 */
export const templatesService = {
  /**
   * Fetches a paginated list of templates by calling the API.
   */
  fetchTemplates: async (params: FetchTemplatesParams = {}): Promise<FetchTemplatesResponse> => {
    const queryParams = new URLSearchParams();
    if (params.category) queryParams.append('category', params.category);
    if (params.tag) queryParams.append('tag', params.tag);
    if (params.freebies !== undefined) queryParams.append('freebies', params.freebies.toString());
    if (params.searchTerm) queryParams.append('search', params.searchTerm);
    if (params.sortBy) queryParams.append('sort', params.sortBy);
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());

    const fullUrl = `${getBaseUrl()}/api/templates?${queryParams.toString()}`;
    const response = await apiGet<FetchTemplatesResponse>(fullUrl);
    
    if (!response.data) {
      throw new Error('Failed to fetch templates');
    }
    
    return response.data;
  },

  /**
   * Fetches a single template by calling the API. For client-side use.
   */
  fetchTemplateBySlug: async (slug: string): Promise<Template> => {
    const fullUrl = `${getBaseUrl()}/api/templates/${slug}`;
    const response = await apiGet<{data: Template}>(fullUrl);
    
    if (!response.data?.data) {
      throw new Error('Template not found');
    }
    
    return response.data.data;
  },

  /**
   * Requests a secure download URL for a specific file by calling the API.
   */
  downloadFile: async (fileId: string): Promise<FileDownloadInfo> => {
    if (!fileId) throw new Error('A file ID is required for a download URL.');
    const fullUrl = `${getBaseUrl()}/api/templates/files/${fileId}/download`;
    const response = await fetch(fullUrl);
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Download failed.' }));
      throw new Error(errorData.error);
    }
    return response.json();
  },

  /**
   * Fetch template SEO data for metadata generation
   */
  fetchTemplateSeoData: async (slug: string): Promise<TemplateSeoData> => {
    const fullUrl = `${getBaseUrl()}/api/templates/${slug}/seo`;
    const response = await apiGet<{data: TemplateSeoData}>(fullUrl);
    
    if (!response.data?.data) {
      throw new Error('Template SEO data not found');
    }
    
    return response.data.data;
  },

  /**
   * Fetch active template categories for public filtering
   */
  fetchActiveCategories: async (): Promise<TemplateCategory[]> => {
    const fullUrl = `${getBaseUrl()}/api/template-categories`;
    const response = await apiGet<{data: TemplateCategory[]}>(fullUrl);
    
    if (!response.data?.data) {
      throw new Error('Failed to fetch template categories');
    }
    
    return response.data.data;
  },
};

// // app/features/templates/services_and_hooks/templatesService.ts

// import { createClient as createServerSupabaseClient } from '@/utils/supabase/server';
// import {
//   FetchTemplatesParams,
//   FetchTemplatesResponse,
//   FileDownloadInfo,
//   Template,
//   TemplateSeoData,
// } from '../templates.interface';

// const API_BASE_PATH = '/api/templates';

// /**
//  * Gets the base URL for API requests.
//  * Uses a relative path on the client and the full URL on the server for client-side fetches.
//  */
// const getBaseUrl = (): string => {
//   if (typeof window === 'undefined') {
//     // This is used by client-side fetch calls that might be rendered on the server initially.
//     return process.env.NEXT_PUBLIC_APP_URL || 'http://localhost:3000';
//   }
//   return ''; // Use relative paths on the client-side
// };

// /**
//  * Service object for all template-related API interactions.
//  * Adheres to the principle that frontend services call API routes,
//  * with a single, clearly marked exception for server-side data loading to avoid HTTP loopbacks.
//  */
// export const templatesService = {
//   /**
//    * Fetches a paginated list of templates by calling the API.
//    * FOR CLIENT-SIDE USE.
//    */
//   fetchTemplates: async (params: FetchTemplatesParams = {}): Promise<FetchTemplatesResponse> => {
//     const queryParams = new URLSearchParams();
//     if (params.category) queryParams.append('category', params.category);
//     if (params.tag) queryParams.append('tag', params.tag);
//     if (params.freebies !== undefined) queryParams.append('freebies', String(params.freebies));
//     if (params.searchTerm) queryParams.append('search', params.searchTerm);
//     if (params.page) queryParams.append('page', String(params.page));
//     if (params.limit) queryParams.append('limit', String(params.limit));

//     const fullUrl = `${getBaseUrl()}${API_BASE_PATH}?${queryParams.toString()}`;
//     const response = await fetch(fullUrl);

//     if (!response.ok) {
//       const errorData = await response.json().catch(() => ({ message: 'Failed to fetch templates' }));
//       throw new Error(errorData.error || 'Failed to fetch templates');
//     }
//     return response.json();
//   },

//   /**
//    * Fetches a single template by calling the API.
//    * FOR CLIENT-SIDE USE (e.g., in a React Query hook).
//    */
//   fetchTemplateBySlug: async (slug: string): Promise<Template> => {
//     if (!slug) throw new Error('A slug is required to fetch a template.');
//     const fullUrl = `${getBaseUrl()}${API_BASE_PATH}/${slug}`;
//     const response = await fetch(fullUrl);
//     if (!response.ok) {
//       const errorData = await response.json().catch(() => ({ message: `Template with slug "${slug}" not found.` }));
//       throw new Error(errorData.error || `Failed to fetch template: ${slug}`);
//     }
//     return response.json();
//   },

//   /**
//    * Fetches a template directly from the database.
//    * THIS IS A SERVER-ONLY FUNCTION to be used in Server Components, `generateMetadata`, and `generateStaticParams`.
//    * It correctly avoids making an HTTP request to its own API from the server.
//    */
//   fetchTemplateBySlug_SERVER: async (slug: string): Promise<Template | null> => {
//     if (!slug) return null;
//     console.log(`[SERVER_SERVICE] Fetching template directly from DB for slug: ${slug}`);
//     try {
//       const supabase = createServerSupabaseClient(); // Uses the server client
      
//       const { data: template, error } = await supabase
//         .from('templates')
//         .select(`*, template_files(*)`) // Select template and all its files
//         .eq('slug', slug)
//         .eq('is_published', true)
//         .single();

//       if (error) {
//         console.error(`[SERVER_SERVICE_ERROR] DB error for slug "${slug}":`, error.message);
//         return null;
//       }
//       return template as Template;
//     } catch (error) {
//       console.error(`[SERVER_SERVICE_FATAL] Exception for slug "${slug}":`, error);
//       return null;
//     }
//   },

//   /**
//    * Requests a secure download URL for a specific file by calling the API.
//    * FOR CLIENT-SIDE USE.
//    */
//   downloadFile: async (fileId: string): Promise<FileDownloadInfo> => {
//     if (!fileId) throw new Error('A file ID is required to get a download URL.');
//     // This API route MUST exist. We will create it next.
//     const fullUrl = `${getBaseUrl()}/api/templates/files/${fileId}/download`;
//     const response = await fetch(fullUrl);
//     if (!response.ok) {
//       const errorData = await response.json().catch(() => ({ error: 'Download failed.' }));
//       throw new Error(errorData.error || 'Could not initiate download.');
//     }
//     return response.json();
//   },
// };